import type { AppWithStats } from '@/core/types';

import { AppTile } from './AppTile';

/**
 * Display names for the languages a deck can teach.
 *
 * Only used as a heading, and only when there is more than one — so the list
 * stays short and adding a language means adding a line here rather than
 * anywhere structural.
 */
const LANGUAGE_NAMES: Record<string, string> = {
  de: 'German',
  'pt-BR': 'Brazilian Portuguese',
};

/**
 * The app tiles, grouped by the language they teach.
 *
 * They used to be one flat grid ordered by `sort_order`, which was right while
 * every deck taught German and became confusing the moment a second language
 * arrived: the two lists number their decks independently, so Substantivos (10)
 * and Gêneros (20) interleaved with Nouns (10) and Der·Die·Das (20) and the
 * home screen read as one jumbled course rather than two.
 *
 * Grouping is skipped entirely for a single language, so nobody studying one
 * thing is shown a heading telling them which thing it is.
 */
export function AppTilesGrid({ apps }: { apps: AppWithStats[] }) {
  const byLanguage = new Map<string, AppWithStats[]>();
  for (const app of apps) {
    byLanguage.set(app.language, [...(byLanguage.get(app.language) ?? []), app]);
  }

  if (byLanguage.size <= 1) {
    return <Grid apps={apps} />;
  }

  return (
    <div className="flex flex-col gap-8">
      {[...byLanguage.entries()].map(([language, group]) => (
        <section key={language} className="flex flex-col gap-3">
          <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {LANGUAGE_NAMES[language] ?? language}
          </h2>
          <Grid apps={group} />
        </section>
      ))}
    </div>
  );
}

function Grid({ apps }: { apps: AppWithStats[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {apps.map((app) => (
        <AppTile key={app.id} app={app} />
      ))}
    </div>
  );
}
